import { Box as ChakraBox, BoxProps as ChakraBoxProps } from '@chakra-ui/react'
import * as React from 'react'

import { BgColorProvider, useBgColorCtx } from '~theme/BgColorProvider'
import { TColorKey } from '~theme/typings'
import { cn } from '~ui/libs/classnames'
import { TTextFontSize } from '~ui/styleGuide/fontStyles'
import { getRadiusValue, TRadius } from '~ui/styleGuide/radiuses'
import { shadows, TInnerShadow, TOuterShadow } from '~ui/styleGuide/shadows'
import { getSpacingValue, TMargin, TSingleSpace, TSpace } from '~ui/styleGuide/spaces'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'
import { TAllChakraProps, TChakraMarginProps, TChakraPaddingProps } from '~ui/typings/chakra'

// all props from chakra that we want to use in our app (TAllChakraProps) minus those we want to replace here with our custom systems
type TPassthroughChakraProps = Omit<
  TAllChakraProps,
  keyof TChakraPaddingProps | keyof TChakraMarginProps
>

// pick those props that are both available on the ChakraBox and we want our component to support them (not override them)
type TChakraProps = Pick<ChakraBoxProps, keyof TPassthroughChakraProps>

type TProps = Children &
  TChakraProps &
  TestingProps & {
    /**
     * background color of the box
     */
    bgColor?: TColorKey
    /**
     * key of radius,
     * or an object with separate key for each corner
     */
    radius?: TRadius
    /**
     * space inside the box,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: TSpace
    /**
     * space outside the box,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    margin?: TMargin
    /**
     * space between the elements of the box (grid-gap),
     * can be single space key,
     */
    gap?: TSingleSpace
    /**
     * vertical space between the elements of the box (grid-gap),
     * can be single space key,
     */
    rowGap?: TSingleSpace
    /**
     * horizontal space between the elements of the box (grid-gap),
     * can be single space key,
     */
    columnGap?: TSingleSpace
    /**
     * key of outer box shadows
     */
    shadow?: TOuterShadow
    /**
     * key of inner box shadows
     */
    shadowInset?: TInnerShadow
    /**
     * css class that will be passed to the html element
     */
    className?: string
    /**
     * key of the predefined text font sizes
     */
    fontSize?: TTextFontSize
    /**
     * a function, that will be called when the element is clicked
     */
    onClick?: PropsOf<typeof ChakraBox>['onClick']
    /**
     * a function, that will be called when the element is hovered
     */
    onPointerEnter?: PropsOf<typeof ChakraBox>['onPointerEnter']
    /**
     * a function, that will be called when the element’s hover state ends
     */
    onPointerLeave?: PropsOf<typeof ChakraBox>['onPointerLeave']
  }

/**
 * actual implementation
 * used inside a BgProviderWrapper bellow
 */
const BoxComponent = React.forwardRef<HTMLDivElement, TProps>(
  (
    {
      gap,
      rowGap,
      columnGap,
      shadow,
      margin,
      radius,
      bgColor,
      padding,
      className,
      shadowInset,
      dataTestId,
      onPointerEnter,
      onPointerLeave,
      ...chakraProps
    },
    ref,
  ) => {
    const { bgColorValue } = useBgColorCtx()

    const radiusValue = radius && getRadiusValue(radius)
    const paddingValue = padding && getSpacingValue(padding)
    const marginValue = margin && getSpacingValue(margin)

    const gapValue = gap && getSpacingValue(gap)
    const rowGapValue = rowGap && getSpacingValue(rowGap)
    const columnGapValue = columnGap && getSpacingValue(columnGap)

    const outerShadowValue = shadow && shadows.outer[shadow]
    const innerShadowValue = shadowInset && shadows.inner[shadowInset]

    const hasBgColorSet = Boolean(bgColor)
    const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

    return (
      <ChakraBox
        {...chakraProps}
        {...themeDesignProps}
        ref={ref}
        data-testid={dataTestId}
        gridGap={gapValue}
        gridRowGap={rowGapValue}
        gridColumnGap={columnGapValue}
        margin={marginValue}
        padding={paddingValue}
        className={cn(className)}
        borderRadius={radiusValue}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave} // onMouseLeave does not fire on disabled elements: https://github.com/facebook/react/issues/18753
        boxShadow={outerShadowValue ?? innerShadowValue}
        backgroundColor={hasBgColorSet ? bgColorValue : undefined}
      />
    )
  },
)

/**
 * the most universal component (a div by default) that accepts a lot of styles as props
 * use instead of some styled components – mostly where there would be an easy styled component to lay some stuff out
 */
export const Box = React.forwardRef<HTMLDivElement, TProps>((props, ref) => {
  // this is just a BgColorProvider wrapper, the actual component is above
  return (
    <BgColorProvider bgColor={props.bgColor ?? null}>
      <BoxComponent ref={ref} {...props} />
    </BgColorProvider>
  )
})

/**
 * the most universal component (a div by default) that accepts a lot of styles as props
 * with display set to `flex`
 * use instead of some styled components – mostly where there would be an easy styled component to lay some stuff out
 */
export const Flex = React.forwardRef<HTMLDivElement, Omit<TProps, 'display'>>((props, ref) => (
  <Box {...props} ref={ref} display={`flex`} />
))
/**
 * the most universal component (a div by default) that accepts a lot of styles as props
 * with display set to `grid`
 * use instead of some styled components – mostly where there would be an easy styled component to lay some stuff out
 */
export const Grid = React.forwardRef<HTMLDivElement, Omit<TProps, 'display'>>((props, ref) => (
  <Box {...props} ref={ref} display={`grid`} />
))
/**
 * the most universal component (a div by default) that accepts a lot of styles as props
 * with display set to `grid` oriented as a single row
 * use instead of some styled components – mostly where there would be an easy styled component to lay some stuff out
 */
export const GridRow = React.forwardRef<HTMLDivElement, Omit<TProps, 'display' | 'gridAutoFlow'>>(
  (props, ref) => <Box {...props} ref={ref} display={`grid`} gridAutoFlow={`column`} />,
)
/**
 * the most universal component (a div by default) that accepts a lot of styles as props
 * with display set to `grid` and placing its items in the very middle (both vertically & horizontally)
 * use instead of some styled components – mostly where there would be an easy styled component to lay some stuff out
 */
export const GridCentered = React.forwardRef<
  HTMLDivElement,
  Omit<TProps, 'display' | 'placeItems'>
>((props, ref) => (
  <Box
    {...props}
    ref={ref}
    display={`grid`}
    placeItems={`center`}
    alignContent={props.alignContent ?? `center`}
  />
))
/**
 * the most universal component (a div by default) that accepts a lot of styles as props
 * with display set to `flex` and placing its items in the very middle (both vertically & horizontally)
 * use instead of some styled components – mostly where there would be an easy styled component to lay some stuff out
 */
export const FlexCentered = React.forwardRef<
  HTMLDivElement,
  Omit<TProps, 'display' | 'alignItems' | 'justifyContent'>
>((props, ref) => (
  <Box {...props} ref={ref} display={`flex`} alignItems={`center`} justifyContent={`center`} />
))
